import { useEffect, useMemo, useState } from 'react';

import { TaughtLesson, useGetTaughtLessonsQuery } from 'shared/api/class-stats';
import { mergeObjectArrays } from 'shared/lib/utils';
// eslint-disable-next-line boundaries/element-types
import { STREAM_ITEMS_LIMIT_PER_QUERY } from 'widgets/stream-preview/constants';

import { filterLessons } from '../lib';

import { LessonFilters } from './types';

export const useTaughtLessons = ({
  selectedClass,
  offset,
  limit,
  subjects,
}: LessonFilters) => {
  const [isAllDataFetched, setIsAllDataFetched] = useState(false);
  const [accumulatedTaughtLessons, setAccumulatedTaughtLessons] = useState<
    TaughtLesson[] | null
  >(null);

  // skip query if class has not been selected (initialized) yet
  const { data: lessons, ...rest } = useGetTaughtLessonsQuery(
    {
      classesBks: selectedClass,
      offset,
      limit,
    },
    { skip: !selectedClass.length },
  );

  useEffect(() => {
    setAccumulatedTaughtLessons((prevState) => {
      const taughtLessonsResponseData = lessons ?? [];
      if (offset) {
        return mergeObjectArrays<TaughtLesson>(
          prevState ?? [],
          taughtLessonsResponseData,
          'id',
        );
      }

      return taughtLessonsResponseData;
    });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [lessons]);

  useEffect(() => {
    if (
      !rest.isFetching &&
      lessons &&
      lessons.length < STREAM_ITEMS_LIMIT_PER_QUERY
    ) {
      setIsAllDataFetched(true);
    }
  }, [lessons, limit, offset, rest.isFetching]);

  // reset isAllDataFetched on class change
  useEffect(() => {
    if (!selectedClass.length) return;

    setIsAllDataFetched(false);
  }, [selectedClass]);

  const taughtLessonsTotalCount = useMemo(() => {
    return accumulatedTaughtLessons ? accumulatedTaughtLessons.length : 0;
  }, [accumulatedTaughtLessons]);

  const filteredLessons = useMemo(
    () => filterLessons(subjects, accumulatedTaughtLessons),
    [accumulatedTaughtLessons, subjects],
  );

  return {
    data: filteredLessons,
    isAllDataFetched,
    taughtLessonsTotalCount,
    ...rest,
  };
};
